import { useState } from 'react';
import {
  useInviteRequests,
  useApproveInviteRequest,
  useDenyInviteRequest,
  type InviteRequest,
} from '../hooks/useInvites';

type StatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'DENIED';

interface InviteRowProps {
  request: InviteRequest;
  onApprove: (id: string, notes?: string) => void;
  onDeny: (id: string, notes?: string) => void;
  isLoading: boolean;
}

function InviteRow({ request, onApprove, onDeny, isLoading }: InviteRowProps) {
  const [showNotes, setShowNotes] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-500/20 text-yellow-400',
      APPROVED: 'bg-green-500/20 text-green-400',
      DENIED: 'bg-red-500/20 text-red-400',
    };
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${styles[status] || ''}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="card mb-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{request.email}</h3>
            {getStatusBadge(request.status)}
            {request.emailVerified ? (
              <span className="rounded-full bg-blue-500/20 px-2 py-1 text-xs font-medium text-blue-400">
                Verified
              </span>
            ) : (
              <span className="rounded-full bg-gray-500/20 px-2 py-1 text-xs font-medium text-gray-400">
                Unverified
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Requested: {formatDate(request.createdAt)}
          </p>
          {request.adminNotes && (
            <div className="mt-2">
              <p className="text-xs text-gray-500">Admin Notes:</p>
              <p className="text-sm text-gray-400">{request.adminNotes}</p>
            </div>
          )}
        </div>

        {request.status === 'PENDING' && (
          <div className="ml-4 flex flex-col gap-2">
            {showNotes ? (
              <div className="w-64 space-y-2">
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Admin notes (optional)"
                  className="input text-sm"
                  rows={2}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onApprove(request.id, adminNotes || undefined)}
                    disabled={isLoading}
                    className="btn-primary flex-1 text-sm"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeny(request.id, adminNotes || undefined)}
                    disabled={isLoading}
                    className="btn-danger flex-1 text-sm"
                  >
                    Deny
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowNotes(false);
                    setAdminNotes('');
                  }}
                  className="w-full text-sm text-gray-400 hover:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onApprove(request.id)}
                  disabled={isLoading}
                  className="btn-primary text-sm"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => onDeny(request.id)}
                  disabled={isLoading}
                  className="btn-danger text-sm"
                >
                  Deny
                </button>
                <button
                  type="button"
                  onClick={() => setShowNotes(true)}
                  className="btn-secondary text-sm"
                  title="Add notes"
                >
                  ...
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function InviteManagement() {
  const [filter, setFilter] = useState<StatusFilter>('PENDING');

  const queryStatus = filter === 'ALL' ? undefined : filter;
  const { data: requests, isLoading, error, refetch } = useInviteRequests(queryStatus as 'PENDING' | 'APPROVED' | 'DENIED' | undefined);

  const approveRequest = useApproveInviteRequest();
  const denyRequest = useDenyInviteRequest();

  const handleApprove = async (id: string, notes?: string) => {
    try {
      await approveRequest.mutateAsync({ id, adminNotes: notes });
    } catch (err) {
      console.error('Failed to approve request:', err);
    }
  };

  const handleDeny = async (id: string, notes?: string) => {
    try {
      await denyRequest.mutateAsync({ id, adminNotes: notes });
    } catch (err) {
      console.error('Failed to deny request:', err);
    }
  };

  const filters: { value: StatusFilter; label: string }[] = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'DENIED', label: 'Denied' },
    { value: 'ALL', label: 'All' },
  ];

  const pendingCount = requests?.filter((r) => r.status === 'PENDING').length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Invite Requests</h2>
          <p className="text-gray-400">Manage user invite requests</p>
        </div>
        <button type="button" onClick={() => refetch()} className="btn-secondary w-full sm:w-auto">
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            type="button"
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {f.label}
            {f.value === 'PENDING' && pendingCount > 0 && (
              <span className="ml-2 rounded-full bg-yellow-500 px-2 py-0.5 text-xs text-black">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="py-12 text-center text-gray-400">Loading invite requests...</div>
      ) : error ? (
        <div className="py-12 text-center text-red-400">
          Failed to load invite requests. Please try again.
        </div>
      ) : requests?.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          No {filter === 'ALL' ? '' : filter.toLowerCase()} invite requests found.
        </div>
      ) : (
        <div>
          {requests?.map((request) => (
            <InviteRow
              key={request.id}
              request={request}
              onApprove={handleApprove}
              onDeny={handleDeny}
              isLoading={approveRequest.isPending || denyRequest.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
